const DAY_MS = 86_400_000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** `YYYY-MM-DD` -> days since the epoch, read as a calendar date with no timezone at all. */
export function dayIndex(day) {
    return Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))) / DAY_MS;
}
/** The inverse of `dayIndex`. */
export function dayFromIndex(index) {
    return new Date(index * DAY_MS).toISOString().slice(0, 10);
}
export function addDays(day, delta) {
    return dayFromIndex(dayIndex(day) + delta);
}
/** The `[startDay, endDay]` a cell covers, or `null` when the cell places nothing. */
export function cellSpan(value) {
    if (value === null || value === undefined || typeof value !== 'object')
        return null;
    const record = value;
    const date = record['date'];
    if (typeof date === 'string' && DAY_RE.test(date)) {
        const time = record['time'];
        return { start: date, end: date, time: typeof time === 'string' ? time.slice(0, 5) : null };
    }
    const from = record['from'];
    const to = record['to'];
    if (typeof from === 'string' && DAY_RE.test(from)) {
        // `from > to` is real data; it places one day on `from` (`[V4§10 E2]`).
        const end = typeof to === 'string' && DAY_RE.test(to) && dayIndex(to) >= dayIndex(from) ? to : from;
        return { start: from, end, time: null };
    }
    return null;
}
export function calendarEvents(input) {
    const first = dayIndex(input.rangeStart);
    const last = dayIndex(input.rangeEnd);
    const maxSpan = input.maxSpan ?? 366;
    const out = [];
    for (const item of input.items) {
        for (const columnId of input.dateColumnIds) {
            const span = cellSpan(item.values[columnId]);
            if (span === null)
                continue;
            const start = dayIndex(span.start);
            const end = Math.min(dayIndex(span.end), start + maxSpan - 1);
            if (end < first || start > last)
                continue;
            const spansDays = end - start + 1;
            for (let index = Math.max(start, first); index <= Math.min(end, last); index += 1) {
                out.push({
                    itemId: item.id,
                    name: item.name,
                    columnId,
                    day: dayFromIndex(index),
                    startDay: span.start,
                    endDay: dayFromIndex(end),
                    time: span.time,
                    spansDays,
                });
            }
        }
    }
    return out;
}
/** The events of each day, keyed `YYYY-MM-DD`, in the order they were produced. */
export function eventsByDay(events) {
    const map = new Map();
    for (const event of events) {
        const list = map.get(event.day);
        if (list)
            list.push(event);
        else
            map.set(event.day, [event]);
    }
    return map;
}
/**
 * The value a drag writes: the same cell shifted by `deltaDays`. `date` keeps its time,
 * `timeline` moves both ends, and a resize moves one (`[V4§6]`).
 */
export function shiftCellValue(value, deltaDays, edge = 'both') {
    const record = value;
    if (record === null || typeof record !== 'object')
        return value;
    if (typeof record['date'] === 'string') {
        return { ...record, date: addDays(record['date'], deltaDays) };
    }
    if (typeof record['from'] === 'string' && typeof record['to'] === 'string') {
        const from = edge === 'end' ? record['from'] : addDays(record['from'], deltaDays);
        const to = edge === 'start' ? record['to'] : addDays(record['to'], deltaDays);
        // A resize may never invert the range.
        if (dayIndex(to) < dayIndex(from))
            return record;
        return { ...record, from, to };
    }
    return value;
}
//# sourceMappingURL=events.js.map